import React, { useState } from 'react'
const { FlatList, /* Platform */ } = XPReactNative
import { useForm, Controller, Control, useWatch } from 'react-hook-form'
import { ActionButton, ActiveButton, SimpleText, View, XStack, TextInputComponent, XPReactNative, QuestionCard, CustomCheckboxInput, Button } from '@my/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthedRequestFn } from '@my/util'
import { useToastController, XPPlatform as Platform } from '@my/ui'

import { FinancialGoalsModal } from '../../../components/index'
import { InputField, RenderGoals } from '../helpers/Helper'
import { FinancialGoalsUtils } from '../../../utils/financialGoals'

const goalTypes = ['Short term', 'Mid term', 'Long term'] as const

// Group goals by category
const groupGoalsByCategory = (goals: any[]) => {
  return goals?.reduce((acc: any, goal) => {
    if (!acc[goal.category]) {
      acc[goal.category] = []
    }
    acc[goal.category].push(goal)
    return acc
  }, {})
}

interface FinancialGoalsProps {
  control: Control<any>
}

export const FinancialGoals: React.FC<FinancialGoalsProps> = (props) => {
  // States combined into one object
  const [goalState, setGoalState] = useState({
    goalId: '',
    goalType: '',
    modalVisible: false,
    isDatePickerVisible: false,
  })

  // Initialize toast, requests, and query client
  const toast = useToastController()
  const authedRequestFn = useAuthedRequestFn()
  const queryClient = useQueryClient()

  const hasMilestone = useWatch({name: "Goals - Has Milestone to Reach"});

  // Form setup
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      goalName: '',
      targetAmount: '',
      timelineTimestamp: null,
      priority: '',
      description: '',
    },
  })

  // Fetch existing financial goals
  const { data: reviewData } = useQuery({
    queryKey: ['financial-goals'],
    queryFn: () => authedRequestFn('/financial-goals?status=&priority='),
  })

  // Mutations for creating, updating, and deleting goals
  const postFinancialGoal = useMutation({
    mutationFn: (formData: any) =>
      authedRequestFn('/financial-goals', { method: 'POST', body: formData }),
    onSuccess: () => {
      reset()
      queryClient.invalidateQueries({ queryKey: ['financial-goals'] })
      toast.show('Successfully Saved!')
    },
  })

  const updateFinancialGoal = useMutation({
    mutationFn: (payload: any) =>
      authedRequestFn(`/financial-goals/${goalState.goalId}`, {
        method: 'PUT',
        body: payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-goals'] })
      toast.show('Goal Updated!')
    },
  })

  // Handlers for form submission and modal control
  const onClose = () => {
    reset()
    setGoalState((prevState) => ({
      ...prevState,
      goalId: '',
      goalType: '',
      modalVisible: false,
    }))
  }

  const showDatePicker = (mode: 'date' | 'time') => {
    setGoalState((prevState) => ({
      ...prevState,
      isDatePickerVisible: true,
    }))
  }

  const handleSubmitForm = (data: any) => {
    try {
      const goalTypeFromDate = FinancialGoalsUtils.goalCategoryFromTargetDate(data?.timelineTimestamp)
      const payload = {
        goalName: data?.goalName,
        targetAmount: data?.targetAmount,
        targetDate: data?.timelineTimestamp,
        priority: data?.priority,
        description: data?.description,
        category: data.goalTerm || goalTypeFromDate,
      }

      if (goalState.goalId) {
        updateFinancialGoal.mutate(payload)
      } else {
        postFinancialGoal.mutate(payload)
      }

      setGoalState((prevState) => ({
        ...prevState,
        modalVisible: false,
      }))
    } catch (error) {
      console.error(error)
    }
  }

  const onGoalPress = (item: any) => {
    setGoalState((prevState) => ({
      ...prevState,
      goalId: item?.goalId,
      goalType: item?.category,
      modalVisible: true,
    }))

    setValue('description', item.description)
    setValue('goalName', item?.goalName)
    setValue('priority', item?.priority)
    setValue('targetAmount', `${item?.targetAmount}`)
    setValue('timelineTimestamp', item?.targetDate)
  }

  const onPressAddNewGoal = (item: any) => {
    setGoalState((prevState) => ({
      ...prevState,
      goalType: item,
      modalVisible: true,
    }))
  }

  const groupedGoals = groupGoalsByCategory(reviewData?.financialGoals)

  // Render Form Input Components

  const renderTextInput = (
      name: keyof typeof getValues,
      placeholder: string,
      rules?: any,
      props?: any
    ) => {
      return (
        <InputField
          control={control}
          name={name}
          rules={rules}
          placeholder={placeholder}
          showPlaceholder
          disableLabel
        />
      )
    }
    // Render priority buttons for selecting goal priority
    const renderPriorityButtons = () => (
      <Controller
        control={control}
        name="priority"
        rules={{ required: 'Priority level is required' }}
        render={({ field: { onChange, value } }) => (
          <XStack marginVertical={10} gap={10}>
            {['High', 'Medium', 'Low'].map((item) => (
              <ActiveButton
                key={item}
                //unstyled
                style={{ width: 88, height: 34, padding: 0}}
                variant={value === item ? undefined : 'outlined'}
                onPress={() => onChange(item)}
                ButtonTextProps={{children: item}}
              />
            ))}
          </XStack>
        )}
      />
    )

  return (
    <>
      <View width={'95%'} padding={15} justifyContent={'center'}>
        <FlatList
          data={goalTypes}
          keyExtractor={(item) => item}
          contentContainerStyle={{ gap: 20 }}
          renderItem={({ item }) => {
            const categoryGoals = groupedGoals?.[item]
            return (
              <RenderGoals
                type={item}
                reviewData={categoryGoals}
                onPress={() => onPressAddNewGoal(item)}
                onGoalPress={onGoalPress}
              />
            )
          }}
        />
      </View>
      <FinancialGoalsModal
        modalVisible={goalState.modalVisible}
        control={control}
        handleSubmit={handleSubmit}
        renderTextInput={renderTextInput}
        renderPriorityButtons={renderPriorityButtons}
        handleSubmitForm={handleSubmitForm}
        //isDatePickerVisible={goalState.isDatePickerVisible}
        //showDatePicker={showDatePicker}
        //goalType={goalState.goalType}
        setValue={setValue}
        onClose={onClose}
      />
      <QuestionCard questionGroupTitle="What are your concerns about your financial future?">
        <InputField
          control={props.control}
          name="Goals - Concerns about Future - Description"
          placeholder="Please describe" // Updated placeholder for clarity
          //rules={{ required: true }} // Required field validation
          height={100}
          multiline={true} // Allow multiline input
        />
      </QuestionCard>
      <QuestionCard questionGroupTitle="Is there a specific financial milestone you are particularly anxious to reach (e.g., paying off debt, buying a home)?">
        <CustomCheckboxInput
          options={[
            { label: 'Yes', value: true },
            { label: 'No', value: false },
          ]}
          control={props.control}
          name="Goals - Has Milestone to Reach"
        />
        {hasMilestone && <InputField
          control={props.control}
          name="Goals - Milestone to Reach - Description"
          placeholder="Please describe" // Updated placeholder for clarity
          //rules={{ required: true }} // Required field validation
          height={100}
          multiline={true} // Allow multiline input
          disabled={!hasMilestone}
        />}
      </QuestionCard>
    </>
  )
}
